import { ROUTES } from "../../constants/routes";
import { RouteItem } from "../../interfaces/routes.interface";
import { ITenantFeatureAttributes } from "../../interfaces/tenant-feature.interface";
import { adminRoutes } from "./admin-routes";

import { dispensaryBudtenderRoutes, userRoutes } from "./user-routes";
import { ORGANIZATION_TYPE_ENUM } from '../../enums/organization-type.enum';
import { IRole } from "../../interfaces/role.interface";

// Mirrors the backend mock-delivery route group, which narrows the tenant
// group's roles down to these two.
const DELIVERY_SETTINGS_ROLE_NAMES = new Set(['Admin', 'Organization Admin']);

const validateRoute = (route: RouteItem, features?: ITenantFeatureAttributes, mixpanelProjectUrl?: string, organizationType?: ORGANIZATION_TYPE_ENUM, role?: IRole, isSandbox?: boolean) => {
    if (route.organizationType && !route.organizationType.includes(organizationType as ORGANIZATION_TYPE_ENUM)) return false;
    if ([ROUTES.USER.sandbox_tools.key, ROUTES.USER.aiq_flow_events.key].includes(route.id) && !isSandbox) return false;
    if (!features?.is_coupons_enabled && route.id === ROUTES.USER.coupons.key) return false;
    if (!features?.is_orders_enabled && route.id === ROUTES.USER.orders.key) return false;
    if (!features?.is_rewards_enabled && !features?.is_custom_rewards_enabled && route.id === ROUTES.USER.rewards.key) return false;
    if (!features?.is_micro_currency_enabled && route.id === ROUTES.USER.micro_currencies.key) return false;
    if (!features?.is_achievements_enabled && route.id === ROUTES.USER.achievements.key) return false;
    if (!features?.is_tiered_achievements_enabled && [ROUTES.USER.tiered_achievements_create.key, ROUTES.USER.tiered_achievements_edit.key].includes(route.id)) return false;
    if (!features?.is_punchcards_enabled && route.id === ROUTES.USER.punchcards.key) return false;
    if ((!features?.is_games_enabled && !features?.is_daily_rewards_enabled) && [ROUTES.USER.games.key, ROUTES.USER.games_dashboard.key, ROUTES.USER.game_entries.key].includes(route.id)) return false;
    if (!features?.is_geofencing_enabled && route.id === ROUTES.USER.geofences.key) return false;
    if (!features?.is_daily_rewards_enabled && route.id === ROUTES.USER.daily_rewards.key) return false;
    if (!features?.is_referrals_enabled && [ROUTES.USER.reports.key, ROUTES.USER.referral_report.key].includes(route.id)) return false;
    if (!features?.is_gift_card_enabled && route.id === ROUTES.USER.gift_card_settings.key) return false;
    if (!features?.is_rewards_enabled && !features?.is_custom_rewards_enabled && !features?.is_gift_card_enabled && !features?.is_micro_currency_enabled && route.id === ROUTES.USER.gamification.key) return false;
    if (!features?.is_sweepstakes_enabled && [ROUTES.USER.sweepstakes.key, ROUTES.USER.sweepstakes_create.key, ROUTES.USER.sweepstakes_edit.key].includes(route.id)) return false;
    if (route.id === ROUTES.USER.analytics.key && !mixpanelProjectUrl) return false;
    if (!features?.is_tiers_enabled && [ROUTES.USER.tiers.key, ROUTES.USER.tiers_create.key, ROUTES.USER.tiers_edit.key].includes(route.id)) return false;
    if (!features?.is_brand_boost_enabled && route.id === ROUTES.USER.brand_boosts.key) return false;
    if (!features?.is_engagement_enabled && route.id === ROUTES.USER.engagement.key) return false;

    if (route.id === ROUTES.USER.organization_management.key && role?.attributes.name !== 'Organization Admin') return false;
    return !route.hidden && !route.isSecondaryItem
}

const PLATFORM_LOCKED_PATHS = [
    '/stores',
    '/notifications',
    '/geofences',
    '/punchcards',
    '/rewards',
    '/achievements',
    '/games',
    '/game-events',
    '/game-entries',
    '/sweepstakes',
    '/tiers',
    '/brand-boosts',
    '/coupons',
    '/orders',
    '/micro-currencies',
    '/daily-rewards',
    '/referrals',
    '/reports',
    '/referral-report',
    '/gift-card-settings',
    '/gamification',
    '/analytics',
];

export const getUserNavigation = (organizationType: ORGANIZATION_TYPE_ENUM, userType: string, roleName: string, features?: ITenantFeatureAttributes, mixpanelProjectUrl?: string, role?: IRole, platformLocked?: boolean, bypassPlatformLock?: boolean, isSandbox?: boolean) => {
    const adminSideBarPrimary = adminRoutes?.filter(
        (val) => !val.hidden && !val.isSecondaryItem
    );

    const adminSideBarSecondary = adminRoutes?.filter(
        (val) => !val.hidden && val.isSecondaryItem
    );

    const routes = userRoutes;

    let userSideBarItemsPrimary = routes?.filter(
        (val) => {
            const url = validateRoute(val, features, mixpanelProjectUrl, organizationType, role, isSandbox);
            if (url) {
                return url;
            }
            return false;
        }
    );

    userSideBarItemsPrimary = userSideBarItemsPrimary.map((val) => {
        if (val.subItems) {
            val.subItems = val.subItems.filter((subVal) => {
                return validateRoute(subVal, features, mixpanelProjectUrl, organizationType, role, isSandbox);
            });
        }
        if (val.id === ROUTES.USER.analytics.key && mixpanelProjectUrl) {
            val.path = mixpanelProjectUrl;
        }
        return val;
    });

    // The secondary bucket is otherwise unfiltered — validateRoute's final
    // `!route.isSecondaryItem` makes it unusable here, so the one secondary item
    // that needs gating is gated inline. Mock Delivery must be invisible to any
    // role the backend would refuse, and to an org without delivery.
    const userSideBarItemsSecondary = routes?.filter(
        (val) =>
            !val.hidden &&
            val.isSecondaryItem &&
            (val.id !== ROUTES.USER.delivery_settings.key ||
                (!!features?.is_delivery_enabled &&
                    DELIVERY_SETTINGS_ROLE_NAMES.has(role?.attributes.name ?? '')))
    ).map((val) =>
        // Copy rather than mutate: the route objects are shared module state
        val.subItems
            ? {
                ...val,
                subItems: val.subItems.filter(
                    (subVal) => subVal.id !== ROUTES.USER.custom_content_haptics.key || !!features?.is_haptics_enabled
                ),
            }
            : val
    );

    const budtenderSideBarItemsPrimary = dispensaryBudtenderRoutes?.filter(
        (val) => !val.hidden && !val.isSecondaryItem
    );

    if (roleName === 'Budtender') {
        return {
            primary: budtenderSideBarItemsPrimary,
            secondary: [],
        }
    }

    if (userType === "admin") {
        return {
            primary: adminSideBarPrimary,
            secondary: adminSideBarSecondary,
        };
    }

    const result = {
        primary: userSideBarItemsPrimary as (RouteItem & { disabled?: boolean })[],
        secondary: userSideBarItemsSecondary as (RouteItem & { disabled?: boolean })[],
    };

    if (platformLocked && !bypassPlatformLock) {
        const MY_APP_PATH = '/overview';
        const disableItem = (item: any): any => ({
            ...item,
            disabled: item.path !== MY_APP_PATH,
            ...(item.subItems ? {
                subItems: item.subItems.map((sub: any) => ({
                    ...sub,
                    disabled: true,
                }))
            } : {}),
        });

        result.primary = result.primary.map(disableItem);
        result.secondary = result.secondary.map(disableItem);
    }

    return result;
};
